import { Card } from '@/app/_components/core/card/card';
import { SmartLink } from '@/app/_components/core/smart-link';
import {
  PARTNER_CARD_HEIGHT,
  type LogoSize,
} from '@/app/_components/shared/partner-list/utils/logo-size';
import { cn, emptyToUndefined } from '@/app/_lib/utils';
import type { PartnerItem } from '@/entities/models/page';
import { PartnerImage } from './partner-image';

interface PartnerCardProps {
  partner: PartnerItem;
  logoSize?: LogoSize;
  trackingPrefix?: string;
}

const getCardClasses = (logoSize: LogoSize) =>
  cn(
    // min-w-0 overrides the base Card's min-w-card so the card wraps the logo
    // at its natural width; gap-0 drops the base card's flex gap (only one child).
    'flex min-w-0 flex-col items-center justify-center gap-0 p-3 shadow-none border-none',
    // Large logos render without a card surface; small keeps the muted background.
    logoSize === 'large' ? 'bg-transparent' : 'bg-muted',
    PARTNER_CARD_HEIGHT[logoSize],
    'rounded-partner-card-radius'
  );

export const PartnerCard = ({
  partner,
  logoSize = 'small',
  trackingPrefix,
}: PartnerCardProps) => {
  const hasImage = !!partner.imageUrl || !!partner.srcsetCandidates;

  const innerContent = (
    <>
      {hasImage && (
        <PartnerImage
          imageUrl={partner.imageUrl}
          imageWidth={partner.imageWidth}
          imageHeight={partner.imageHeight}
          srcsetCandidates={partner.srcsetCandidates}
          altText={partner.altText}
          title={partner.title}
          text={partner.text}
          logoSize={logoSize}
        />
      )}
      {!hasImage && partner.text && (
        <div className="line-clamp-2 text-center text-sm font-bold">
          {partner.text}
        </div>
      )}
    </>
  );

  const content = <Card className={getCardClasses(logoSize)}>{innerContent}</Card>;

  const linkLabel =
    emptyToUndefined(partner.title) ??
    emptyToUndefined(partner.text) ??
    emptyToUndefined(partner.altText);

  if (partner.link) {
    return (
      <SmartLink
        href={partner.link}
        className="block"
        title={linkLabel}
        data-track-id={trackingPrefix ? `${trackingPrefix}-item` : undefined}
      >
        {content}
      </SmartLink>
    );
  }

  return <>{content}</>;
};
